import { IconArrowDown, IconArrowUp } from "@tabler/icons-react";
import type { FC, ReactNode } from "react";
import {
  Cell,
  Collection,
  Column,
  type Key,
  Link,
  type LinkProps,
  Row,
  type SortDescriptor,
  Table,
  TableBody,
  TableHeader,
} from "react-aria-components";
import { tv } from "tailwind-variants";
import { Size } from "../helpers/helpers.js";
import { useInfiniteScroll } from "../helpers/useInfiniteScroll.js";
import { useLinkRender } from "../Link/routerLink.js";
import { nestedControlsStyle } from "../nestedControls.js";
import { TableSkeletonContent } from "../Skeletons/skeletons.js";
import { EntityTableEmptyState } from "./entityTableEmptyState.js";

/** Styles for a table with comfortable spacing and low information density, used for projects, teammates, etc. */
export const entityTable = tv({
  slots: {
    table: "isolate w-full text-sm shadow-sm ring-1 ring-black/5",
    header: "sticky top-0 z-10 border-b border-border-light",
    column:
      "truncate bg-gray-50 py-3 text-left text-xs font-semibold text-foreground data-[allows-sorting]:cursor-pointer data-[allows-sorting]:hover:text-muted-foreground",
    body: "divide-y divide-gray-200",
    row: "group/row data-[react-aria-pressable]:cursor-pointer",
    cell: "bg-white group-data-[react-aria-pressable]/row:group-hover/row:bg-blue-50",
    empty: "bg-white",
  },
  variants: {
    // When rows act as links (via `EntityTableRowLink` in a cell) the row needs
    // to be the positioned ancestor so the link's `::before` overlay can expand
    // to cover it. We also mirror the hover/cursor affordance that pressable
    // rows get, since these rows aren't `react-aria` pressable.
    hasRowLinks: {
      true: {
        row: nestedControlsStyle.container(),
        cell: "group-[:hover]/row:bg-blue-50",
      },
    },
    size: {
      [Size.MEDIUM]: {
        column: "px-3 sm:px-6",
        cell: "px-3 py-2 sm:px-6 sm:py-4",
      },
      [Size.SMALL]: {
        column: "px-3 sm:px-4",
        cell: "px-3 py-2 sm:px-4",
      },
    },
    isFullBleed: {
      false: {
        table: "rounded-md",
        column: "first:rounded-tl-md last:rounded-tr-md",
        empty: "rounded-b-md",
      },
      true: {},
    },
    isLastRow: {
      true: {},
    },
  },
  compoundVariants: [
    {
      isFullBleed: false,
      isLastRow: true,
      class: {
        cell: "first:rounded-bl-md last:rounded-br-md",
      },
    },
  ],
  defaultVariants: {
    size: Size.MEDIUM,
    isFullBleed: false,
    hasRowLinks: false,
  },
});

export interface EntityTableProps<T extends Entity> {
  /** Accessibility label for the table. */
  "aria-label": string;
  /** Should be stable to avoid frequent re-rendering of the table. */
  columns: readonly EntityTableColumnDefinition<T>[];
  entities: readonly T[] | undefined;
  /** Optional hint to help trigger pagination after more entities were loaded but all are hidden from view. */
  unfilteredEntityCount?: number;
  /** Whether entities are loading. */
  isLoading?: boolean;
  /** Whether an error occurred while loading entities. */
  isError?: boolean;
  /** A callback to load more entities, if more are available. */
  onLoadMore?: () => void;
  /** Message to show when there are no entities (and we're not loading or encountered an error). */
  emptyMessage: ReactNode;
  /**
   * Called when the user activates a row (click, Enter, etc.).
   *
   * Prefer `hasRowLinks` + `EntityTableRowLink` when activating a row should
   * navigate somewhere, so users can cmd/ctrl/right/middle-click the row.
   */
  onEntityAction?: (entity: T) => void;
  /**
   * Set this when rows navigate somewhere via an `EntityTableRowLink` rendered
   * in one of the columns. It gives the row the positioning required for the
   * link's overlay to cover the whole row, plus the hover/cursor affordance.
   */
  hasRowLinks?: boolean;
  /** Size variant for the table, affecting spacing and information density. */
  size?: typeof Size.SMALL | typeof Size.MEDIUM;
  /**
   * Optional per-row emphasis: returns extra classes merged onto every cell in
   * the row (cells carry the row's background, so this is where a row shade must
   * land). Use for subtle row-level distinction, e.g. shading a secondary row.
   */
  getRowClassName?: (entity: T) => string | undefined;
  /** Whether the table will be rendered full bleed and therefore should not have rounded corners. */
  isFullBleed?: boolean;
  /** Current sort descriptor. When provided alongside `onSortChange`, columns with `allowsSorting` show sort affordance. */
  sortDescriptor?: SortDescriptor;
  /** Called when the user clicks a sortable column header. */
  onSortChange?: (descriptor: SortDescriptor) => void;
}

interface Entity {
  id: string;
}

export interface EntityTableColumnDefinition<T extends Entity> {
  id: string;
  /** `className` for the column header cell. */
  className?: string;
  /** Content for the column header cell. */
  header: ReactNode;
  /** Whether this column acts as a header for the row. */
  isRowHeader?: boolean;
  /** Render text value for the cell. Used for search and type-ahead. */
  getTextValue?: (entity: T) => string;
  /** Whether this column header is clickable to change the sort order. */
  allowsSorting?: boolean;
  /** Render content for the column data cell.  */
  renderCell: (entity: T) => ReactNode;
}

const NO_ENTITIES = Object.freeze([]);

/**
 * Simple all-batteries-included table that can be used for projects, teammates, etc.
 *
 * Rows can either trigger an action via `onEntityAction`, or navigate somewhere
 * by setting `hasRowLinks` and rendering an {@link EntityTableRowLink} in one of
 * the columns (typically the row header). The latter is preferable for
 * navigation because the row becomes a real link, so users can
 * cmd/ctrl/right/middle-click it to open it in a new tab.
 *
 * If you need something even slightly more complicated, don't hesitate to fork this component and reuse its parts.
 */
export const EntityTable = <T extends Entity>(
  props: EntityTableProps<T>,
): ReactNode => {
  const { canLoadMore, loadMoreSentinelRef } = useInfiniteScroll(props);

  const { entities = NO_ENTITIES, onEntityAction } = props;
  const styles = entityTable({
    size: props.size,
    isFullBleed: props.isFullBleed,
    hasRowLinks: props.hasRowLinks,
  });

  const onRowAction = onEntityAction
    ? (key: Key) => {
        const entity = entities.find((e) => e.id === key);
        if (entity) {
          onEntityAction(entity);
        }
      }
    : undefined;

  return (
    <Table
      aria-label={props["aria-label"]}
      className={styles.table()}
      onRowAction={onRowAction}
      sortDescriptor={props.sortDescriptor}
      onSortChange={props.onSortChange}
    >
      <TableHeader columns={props.columns} className={styles.header()}>
        {(column) => (
          <Column
            id={column.id}
            isRowHeader={column.isRowHeader}
            allowsSorting={column.allowsSorting}
            className={styles.column({
              className: column.className,
            })}
          >
            {({ allowsSorting, sortDirection }) =>
              allowsSorting ? (
                <div className="flex items-center gap-1">
                  {column.header}
                  {sortDirection !== undefined ? (
                    sortDirection === "ascending" ? (
                      <IconArrowDown className="size-3 shrink-0 text-gray-400" />
                    ) : (
                      <IconArrowUp className="size-3 shrink-0 text-gray-400" />
                    )
                  ) : null}
                </div>
              ) : (
                column.header
              )
            }
          </Column>
        )}
      </TableHeader>

      <TableBody
        className={styles.body()}
        renderEmptyState={() => (
          <EntityTableEmptyState
            className={styles.empty()}
            isLoading={props.isLoading}
            isError={props.isError}
          >
            {props.emptyMessage}
          </EntityTableEmptyState>
        )}
      >
        <Collection
          items={entities}
          dependencies={[props.columns, props.getRowClassName]}
        >
          {(entity) => (
            <Row columns={props.columns} className={styles.row()}>
              {(column) => (
                <Cell
                  className={styles.cell({
                    isLastRow: entity === entities.at(-1),
                    className: props.getRowClassName?.(entity),
                  })}
                  textValue={column.getTextValue?.(entity)}
                >
                  {column.renderCell(entity)}
                </Cell>
              )}
            </Row>
          )}
        </Collection>

        {canLoadMore ? (
          <Row>
            <Cell colSpan={props.columns.length}>
              <div ref={loadMoreSentinelRef}>
                <TableSkeletonContent rowCount={1} />
              </div>
            </Cell>
          </Row>
        ) : null}
      </TableBody>
    </Table>
  );
};

export interface EntityTableRowLinkProps extends Omit<
  LinkProps,
  "children" | "className" | "href"
> {
  /** Destination for the row link. */
  href: string;
  /** Additional classes for the link, merged with the row-overlay styles. */
  className?: string;
  children: ReactNode;
}

/**
 * A link to render inside an {@link EntityTable} cell (typically the row-header
 * column) when the table has `hasRowLinks`. Its invisible `::before` overlay
 * expands to cover the whole row — see {@link nestedControlsStyle} — so the
 * entire row acts as a single real link.
 *
 * Because it renders a real anchor, users can cmd/ctrl/right/middle-click the
 * row to open the link in a new tab. Any other interactive content in the row
 * (buttons, menus, secondary links) must be layered above the overlay with
 * `nestedControlsStyle.secondaryControl()`, otherwise the overlay will swallow
 * its clicks.
 */
export const EntityTableRowLink: FC<EntityTableRowLinkProps> = ({
  className,
  ...props
}): ReactNode => {
  const renderLink = useLinkRender();

  return (
    <Link
      {...props}
      className={nestedControlsStyle.primaryControl({ className })}
      render={props.isDisabled ? undefined : renderLink}
    />
  );
};
